import firebase from 'firebase/app';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { ProgressConfigType } from '../../components/OfferRelatedWrapper/OfferProgress';
import { OfferRelatedWrapper } from '../../components/OfferRelatedWrapper/OfferRelatedWrapper';
import { Typography } from '../../components/Typography/Typography';
import { onLogOfferSubmitConfirm } from '../../events/hooks/onLogOfferSubmitConfirm.hook';
import { firebaseFunctions, firestore } from '../../firebase/firebase';
import {
  FirebaseCallableFunctions,
  FirestoreCollection,
  FunctionSignOfferConfirmPayload,
} from '../../firebase/firebase.models';
import { useGetOffer } from '../../firebase/hooks/getOfferById.hook';
import { getTotalIncome } from '../../firebase/hooks/getTotalIncome.hook';
import { OfferCheck, useUpdateOfferCheck } from '../../firebase/hooks/updateOfferCheck.hook';
import { useGetOfferImages } from '../../hooks/getOfferImages.hook';
import { OfferStatusState } from '../../models/offers.model';
import { WantedListing } from '../../models/wantedListings.model';
import { gtm } from '../../reportGTM';
import { App } from '../App/App';
import { useAuth } from '../Auth/AuthContext';
import { fetchUserData } from '../MojaTablica/Account/Identity/ConfirmIdentity/firebaseIdentityService';
import { createPDFs } from '../OfferAdd/Documents/createPDFs';
import { ConfigType, FilePaths } from '../OfferAdd/Documents/documents.models';
import { simulateStep } from '../OfferAdd/OfferAddConfirm';
import { AppRoutes } from '../Routing/routing.model';
import { ManualSignatureUpload } from '../Shared/ManualSignatureUpload';
import { WantedListingsMatchModal } from '../WantedListings/WantedListingsMatchModal';
import {
  associateOfferWithWantedListings,
  checkForMatchingWantedListings,
} from '../WantedListings/utils/matchWantedListings';

import { ReceptionOfThePremises } from './components/ReceptionOfThePremises';
import { setOfferToUser } from './hooks/setOfferToUser.hook';

interface Props {
  userUid: string;
  offerId: string;
}

export const OfferSubmitConfirm: React.FC<Props> = ({ userUid, offerId }) => {
  const { isAuthenticating, isAuth } = useAuth();
  return <App>{!isAuthenticating && isAuth ? <WhenAuth offerId={offerId} userUid={userUid} /> : <WhenNotAuth />}</App>;
};

const WhenAuth: React.FC<Props> = ({ userUid, offerId }) => {
  const { push } = useHistory();
  const isLoading = useRef(false);
  const [offer] = useGetOffer(offerId);
  const imagesUrl = useGetOfferImages(offerId);
  const [offerAlreadySigned, setOfferAlreadySigned] = useState(false);
  const [isOperationCompleted, setIsOperationCompleted] = useState(false);
  const { currentUser } = useAuth();
  const [landlordData, setLandlordData] = useState(null);
  const { t } = useTranslation();
  const [showWantedListingsModal, setShowWantedListingsModal] = useState(false);
  const [matchingListings, setMatchingListings] = useState<WantedListing[]>([]);
  const [showManualUploadUI, setShowManualUploadUI] = useState(false);
  const [manualUploadCompleted, setManualUploadCompleted] = useState(false);
  const [certificateFileExists, setCertificateFileExists] = useState(false);
  const [checkingFile, setCheckingFile] = useState(true);

  const handleOfferConfirmation = async () => {
    try {
      isLoading.current = true;
      // Fake step: Sprawdzanie bezpieczeństwa sieci
      await simulateStep(offerId, OfferCheck.CHECKING_NETWORK, 1000);

      // Fake step: Pobieranie potrzebnych danych
      await simulateStep(offerId, OfferCheck.DOWNLOADING_DATA, 1500);

      // Real step: Podpisywanie oferty
      useUpdateOfferCheck(offer.id, OfferCheck.SIGNING_CONTRACT);

      // Generuj certyfikaty (backend zajmuje się filtrowaniem czy generować dla manual upload)
      await createPDFs(offer, ConfigType.LEASE);

      // Fake step: Zapisywanie raportu
      await simulateStep(offerId, OfferCheck.SAVE_REPORT, 1600);

      // Przypisanie oferty użytkownikowi
      await setOfferToUser(userUid, offerId);

      // Logowanie potwierdzenia oferty
      await onLogOfferSubmitConfirm(offerId);

      // Real step: Wysyłanie podpisanej umowy
      useUpdateOfferCheck(offer.id, OfferCheck.SENDING_CONTRACT);
      const onOfferSignConfirm = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ON_OFFER_SIGN_CONFIRM);
      const functionSignOfferConfirmPayload: FunctionSignOfferConfirmPayload = { userUid, offerId };
      await onOfferSignConfirm(functionSignOfferConfirmPayload);

      // Oznaczenie operacji jako zakończonej
      useUpdateOfferCheck(offer.id, OfferCheck.DONE);

      // Sprawdź czy to najemca i czy ma wanted listings
      // Dane są hashowane SHA-256, więc musimy porównać hash
      const crypto = await import('crypto');
      const hashedCurrentUid = crypto.createHash('sha256').update(currentUser.uid).digest('hex');
      const isTenant = offer.offerStatus.tenant?.uid === hashedCurrentUid;

      console.log('Checking wanted listings:', {
        isTenant,
        currentUserUid: currentUser.uid,
        hashedCurrentUid,
        tenantUid: offer.offerStatus.tenant?.uid,
        tenantCompanyId: offer.offerStatus.tenant?.companyId,
      });

      if (isTenant && offer.offerStatus.tenant?.companyId) {
        // Pobierz prawdziwe (niezahashowane) companyId z dokumentu użytkownika
        const userDoc = await firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid).get();
        const userData = userDoc.data();
        const realCompanyId = userData?.companyId;

        console.log('Company IDs:', {
          hashedCompanyId: offer.offerStatus.tenant.companyId,
          realCompanyId: realCompanyId,
        });

        if (realCompanyId) {
          console.log('Fetching wanted listings for company:', realCompanyId);
          const matchedListings = await checkForMatchingWantedListings(offer, realCompanyId);
          console.log('Found wanted listings:', matchedListings.length, matchedListings);

          if (matchedListings.length > 0) {
            setMatchingListings(matchedListings);
            setShowWantedListingsModal(true);
            // Nie ustawiaj jeszcze isOperationCompleted - zaczekaj na modal
            return;
          }
        }
      }

      setIsOperationCompleted(true);
      const totalIncome = await getTotalIncome(offer.id);
      const price = typeof offer.price === 'string' ? parseFloat(offer.price) : offer.price;
      const formattedPrice = price && !isNaN(price) ? price.toFixed(2) : '0.00';
      gtm('begin_checkout', {
        currency: 'PLN',
        value: totalIncome,
        items: [
          {
            item_id: offer.id,
            item_name: offer.name,
            price: parseFloat(formattedPrice),
            affiliation: landlordData?.company?.nazwa || '',
            item_category: offer.legalMainUse,
            location_id: offer.placeId,
          },
        ],
      });
    } catch (error) {
      console.error('Error during offer submission process:', error);
      setOfferAlreadySigned(true);
    } finally {
      isLoading.current = false;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!currentUser?.uid) {
        return;
      }
      try {
        const data = await fetchUserData(currentUser.uid);
        if (isMounted) {
          setLandlordData(data);
        }
      } catch (error) {
        console.error('Error fetching landlord data:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]);

  // Sprawdź czy certyfikat już istnieje w storage
  useEffect(() => {
    const checkCertificateExists = async () => {
      if (!offer || !currentUser) {
        return;
      }

      // Nie sprawdzaj po uploadzie - proces trwa w tle
      if (manualUploadCompleted) {
        return;
      }

      // Tylko dla selectedLeaseId === 3 (umowy powyżej 12 miesięcy)
      if (offer.leaseAgreement.selectedLeaseId !== 3) {
        setCheckingFile(false);
        return;
      }

      try {
        setCheckingFile(true);

        // Pobierz companyId użytkownika
        const userDoc = await firebase.firestore().collection('users').doc(currentUser.uid).get();
        const companyId = userDoc.data()?.companyId;

        if (!companyId) {
          setCheckingFile(false);
          setCertificateFileExists(false);
          return;
        }

        // Sprawdź czy plik istnieje w storage
        const storage = firebase.storage();
        const filePath = `companies/${companyId}/offers/${offerId}/${FilePaths.CERTYFIKAT_UMOWY_NAJMU}`;
        const fileRef = storage.ref(filePath);

        try {
          await fileRef.getDownloadURL();
          // Plik istnieje
          setCertificateFileExists(true);
        } catch (error: any) {
          // Plik nie istnieje (error.code === 'storage/object-not-found')
          setCertificateFileExists(false);
        }
      } catch (error) {
        console.error('Error checking certificate file:', error);
        setCertificateFileExists(false);
      } finally {
        setCheckingFile(false);
      }
    };

    checkCertificateExists();
  }, [offer, offerId, currentUser, manualUploadCompleted]);

  useEffect(() => {
    if (isLoading.current || isOperationCompleted) return;
    if (!offer) return;
    if (!userUid || offerId === undefined) {
      push(AppRoutes.HOME, { offerId });
      return;
    }

    // Fixedlong (selectedLeaseId === 3) - umowy powyżej 12 miesięcy
    if (offer.offerStatus.state === OfferStatusState.BOOKED && offer.leaseAgreement.selectedLeaseId === 3) {
      // Jeśli upload został zakończony, nie rób nic - proces już trwa
      if (manualUploadCompleted) {
        return;
      }

      // Jeśli obecnie pokazujemy UI uploadu, nie rób nic - czekamy na akcję użytkownika
      if (showManualUploadUI) {
        return;
      }

      // Jeśli plik już istnieje i NIE pokazujemy UI uploadu, wyślij maila (użytkownik wrócił po uploadzie)
      if (!checkingFile && certificateFileExists && !isLoading.current) {
        handleOfferConfirmation();
        return;
      }

      // Jeśli plik nie istnieje, pokaż UI uploadu
      if (!checkingFile && !certificateFileExists) {
        setShowManualUploadUI(true);
        return;
      }

      // W innych przypadkach (np. checkingFile = true) czekaj
      return;
    }

    // Standardowy flow - dla infinite/fixedshort
    if (offer.offerStatus.state === OfferStatusState.BOOKED && !isLoading.current) {
      isLoading.current = true;
      handleOfferConfirmation();
    } else if (offer.offerStatus.state !== OfferStatusState.BOOKED) {
      setOfferAlreadySigned(true);
    }
  }, [offer, offerId, userUid, isOperationCompleted, push, manualUploadCompleted, checkingFile, certificateFileExists]);

  // Pokazuj ManualSignatureUpload UI gdy wykryto fixedlong
  if (showManualUploadUI && offer) {
    return (
      <App>
        <ManualSignatureUpload
          offerId={offerId}
          offer={offer}
          userRole="tenant"
          sourceFileName={FilePaths.CERTYFIKAT_POWYZEJ_12}
          targetFileName={FilePaths.CERTYFIKAT_UMOWY_NAJMU}
          onUploadComplete={async () => {
            setManualUploadCompleted(true);
            setShowManualUploadUI(false);
            await handleOfferConfirmation();
          }}
        />
      </App>
    );
  }

  return (
    <>
      {offer && (
        <OfferRelatedWrapper
          images={imagesUrl}
          offer={offer}
          description={
            <>
              <Typography.H5>
                {offerAlreadySigned ? t('RENTAL_AGREEMENT.RECEIVE_PROPERTY') : t('RENTAL_AGREEMENT.AGREEMENT_SIGNED')}
              </Typography.H5>
              <Typography.Body1>{t('RENTAL_AGREEMENT.AUTHORIZED_PERSON')}</Typography.Body1>
              <ReceptionOfThePremises id={offerId} offer={offer} />
            </>
          }
          progressConfigType={ProgressConfigType.SIGN_SUBMIT}></OfferRelatedWrapper>
      )}
      {showWantedListingsModal && (
        <WantedListingsMatchModal
          visible={showWantedListingsModal}
          matchingListings={matchingListings}
          offer={offer}
          onClose={() => {
            setShowWantedListingsModal(false);
            setIsOperationCompleted(true);
          }}
          onSubmit={async (selectedListings) => {
            await associateOfferWithWantedListings(offer.id, selectedListings, currentUser.uid);
            setShowWantedListingsModal(false);
            setIsOperationCompleted(true);
          }}
        />
      )}
    </>
  );
};

const WhenNotAuth: React.FC = () => {
  const { showSignInModal, closeModal } = useAuth();

  useEffect(() => {
    showSignInModal();
    return () => closeModal();
  }, []);

  return null;
};
